















































































import Vue from 'vue';
import Component from 'vue-class-component';
// eslint-disable-next-line no-unused-vars
import { Emit, Prop, Watch } from 'vue-property-decorator';

import GenealogyPersonSearchInput from '@/components/genealogy/GenealogyPersonSearchInput.vue';
import WikitreeController from '@/controllers/WikitreeGenealogyController';
import Connection from '@/models/Connection';
import { GenealogyPerson, nodeToPerson } from '@/models/Genealogy';
import Node from '@/models/Node';
import { PacketType } from '@/models/Packet';
import { SendPacket } from '@/networking/SendPacket';
import Mutations from '@/store/mutations';
import { mapGetters } from 'vuex';

@Component({
    components:
    {
        GenealogyPersonSearchInput
    },
    computed: 
	{
	    ...mapGetters([
	        'timelines',
	        'nodes'
	    ])
	}
})
export default class GenealogyImporter extends Vue
{
    @Prop({ default: false, type: Boolean }) readonly disabled!: boolean;
    @Prop({ default: false, type: Boolean }) readonly eventLimitReached!: boolean;
    @Prop({ default: 0, type: Number }) readonly timelineid!: number;

    submitting = false;

    selectedPerson : GenealogyPerson | null = null;
    generationsBefore : number = 0;
    generationsAfter : number = 0;
    descendantParents : boolean = false;
    ancestorChildren : boolean = false;
    
    get shouldDisableFields()
    {
        return this.submitting || this.disabled || this.selectedPerson == null;
    }

    get selectedPersonName()
    {
        return (this.selectedPerson != null) 
            ? this.selectedPerson.long_name || `${this.selectedPerson.first_name} ${this.selectedPerson.last_name}`
            : '';
    }

    async importPerson(
        person : GenealogyPerson,
        // source is for avoiding backtracking while traversing the tree
        source : GenealogyPerson | null = null) : Promise<any>
    {
        // first check the free tier event limit
        if (this.eventLimitReached)
        {
            this.$emit('show-event-limit');
            return;
        }

        let node = new Node();
        node.data = {
            genealogyProvider: person.provider_type,
            genealogyId: person.id,
            genealogyName: person.full_name,
        };
        node.type = Node.NodeType.TimelineEvent;
        node.name = person.long_name || `${person.first_name} ${person.last_name}`;
        node.dateStart = person.birth_date || person.death_date || new Date(Date.now());
        node.dateEnd = person.death_date || node.dateStart;
        node.image = person.photo;
        node.description = person.bio;
        node.references = person.sources;
        // TODO: do something with person.birth_location, person.gender, and person.is_alive?

        const providerIdLookup = (node.data && (node.data as any).genealogyProvider
            && ((node.data as any).genealogyId || (node.data as any).genealogyName))
            ? `${(node.data as any).genealogyProvider}---${(node.data as any).genealogyId}---${(node.data as any).genealogyName}`
            : null;
        const namebirthLookup = `${node.name}${node.dateStart.valueOf()}`;
        const n = (providerIdLookup && this.providerIdNodeMap.has(providerIdLookup))
            ? this.providerIdNodeMap.get(providerIdLookup)
            : (this.namebirthNodeMap.has(namebirthLookup) // TODO: should we phase this out?
                ? this.namebirthNodeMap.get(namebirthLookup)
                : await Node.createAndWait(node, this.timelineid));
        
        for (let d = 0; d < person.children.length; ++d)
        {
            if (source == null || person.children[d].full_name !== source.full_name)
            {
                const dn = await this.importPerson(person.children[d], person);
                let conn = new Connection(n.nodeid, dn.nodeid);
                this.$store.commit(Mutations.setConnection, conn);
                SendPacket(PacketType.NodeAddConnection, 
                    {
                        nodeid: conn.nodeid1,
                        otherid: conn.nodeid2
                    });
            }
        }
        for (let a = 0; a < person.parents.length; ++a)
        {
            if (source == null || person.parents[a].full_name !== source.full_name)
            {
                const pn = await this.importPerson(person.parents[a], person);
                let conn = new Connection(n.nodeid, pn.nodeid);
                this.$store.commit(Mutations.setConnection, conn);
                SendPacket(PacketType.NodeAddConnection, 
                    {
                        nodeid: conn.nodeid1,
                        otherid: conn.nodeid2
                    });
            }
        }

        return n;
    }

    async submit(ev : Event)
    {
        ev.preventDefault();
        this.submitting = true;

        if (this.generationsBefore < 0)
            this.generationsBefore = 0;
        if (this.generationsBefore > 5)
            this.generationsBefore = 5;
        if (this.generationsAfter < 0)
            this.generationsAfter = 0;
        if (this.generationsAfter > 4)
            this.generationsAfter = 4;

        if (!this.selectedPerson)
        {
            this.submitting = false;
            return false;
        }

        try
        {
            const data = await WikitreeController.LookupPerson(
                this.selectedPerson.full_name,
                this.generationsBefore,
                this.generationsAfter,
                this.descendantParents,
                this.ancestorChildren
            );
            if (data == null)
                return;
            const fullPerson : GenealogyPerson = new GenealogyPerson(data); // TODO: why?
            // console.log(fullPerson);

            const n = await this.importPerson(fullPerson);
            this.$store.commit(Mutations.setSelected, n);
            this.selectedPerson = null;
        }
        catch (err)
        {
            const resp = (err as any).response;
            this.submitting = false;
            return resp.data;
        }

        this.submitting = false;
        return true;
    }

    get timeline()
    {
        return (this as any).timelines.get(this.timelineid);
    }

    get namebirthNodeMap()
    {
        const map = new Map();
        for (const nodeid of this.timeline.nodeIDs)
        {
            const n = (this as any).nodes.get(nodeid);
            map.set(`${n.name}${n.dateStart.valueOf()}`, n);
        }
        return map;
    }

    get providerIdNodeMap()
    {
        const map = new Map();
        for (const nodeid of this.timeline.nodeIDs)
        {
            const n = (this as any).nodes.get(nodeid);
            if (n.data && n.data.genealogyProvider && n.data.genealogyId)
            {
                map.set(`${n.data.genealogyProvider}---${n.data.genealogyId}---${n.data.genealogyName}`, n);
            }
        }
        return map;
    }

    setSelectedPersonFromExistingNode(node: Node)
    {
        this.selectedPerson = nodeToPerson(node);
    }
}
